import React from 'react';
import { Helmet } from 'react-helmet';
import { Button, Card, Col, Collapse, Row } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { GlobalOutlined } from '@ant-design/icons';

import { CatalogAsyncSelect } from 'components/form/selects/CatalogAsyncSelect';
import { CatalogRegionSelect } from 'components/form/selects/CatalogRegionSelect';
import { ContentField } from 'components/form/Content/ContentField';
import { CreateRouteMap } from 'components/form/CreateRouteMap/CreateRouteMap';
import { DurationField } from 'components/form/DurationField/DurationField';
import { FormActions } from 'components/Layout/FormActions/FormActions';
import { InputField } from 'components/form/base/InputField';
import { LangSelectSection } from 'components/form/LangSelectSection/LangSelectSection';
import { MainImage } from 'components/form/MainImage/MainImage';
import { SEO } from 'components/form/SEO/SEO';
import { Status } from 'components/form/Status/Status';
import { TextAreaField } from 'components/form/base/TextAreaField';
import { TouristRoutePoints } from 'components/form/TouristRoutePoints/TouristRoutePoints';
import {
  ContentTypeEnum,
  TContentBlock,
} from 'components/form/Content/contentTypes';
import { Statuses } from 'constants/status';
import {
  ITouristRoutesDraftItem,
  ITouristRoutesItem,
} from 'store/slices/touristRoutes/interfaces';
import { useDeepEffect } from 'utils/useDeepEffect';
import { isEmpty, isString } from 'utils/helpers';
import { getCollapseTabsWithError } from 'utils/yup';
import { TAudio } from 'types/audio';
import { TImage } from 'types/image';
import { TSEO } from 'types/seo';
import { PREVIEW_ASPECT_RATIO } from 'constants/image';
import { LangEnumKeys } from 'constants/lang';

import { mapValues, mapValuesToDraft, validationSchema } from './formUtils';

interface IFormValues extends TSEO {
  audio: TAudio;
  content: TContentBlock[];
  days: number;
  duration: string;
  externalUrl: string;
  heroImage: TImage;
  image: TImage;
  intro?: string;
  isSelection: boolean;
  lang: LangEnumKeys | { value: LangEnumKeys; label: string };
  name: string;
  points: any[];
  polyline: [string, string][];
  routeLevel: number | { value: LangEnumKeys; label: string };
  routeType: number | { value: LangEnumKeys; label: string };
  shortDescription: string;
  status: Statuses;
  tags: number[] | { value: LangEnumKeys; label: string }[];
}

type TProps = {
  initialValues: IFormValues;
  onFinish: (values: ITouristRoutesItem) => void;
  onSaveDraft: (values: ITouristRoutesDraftItem) => void;
  cancelButtonHandler: () => void;
  isDraft: boolean;
  isLoading: boolean;
};

const collapseFieldsErrors = {
  content: 'description',
  routeType: 'description',
  routeLevel: 'description',
  days: 'routeDuration',
  time: 'routeDuration',
};

export const TouristRoutesForm: React.FC<TProps> = ({
  initialValues,
  onFinish,
  onSaveDraft,
  cancelButtonHandler,
  isDraft,
  isLoading,
}) => {
  const [activeCollpaseTabs, setActiveCollpaseTabs] = React.useState([
    'description',
    'points',
    'seo',
    'status',
    'polyline',
    'routeDuration',
    'link',
    'externalUrl',
  ]);
  const resolver = yupResolver(validationSchema);

  const methods = useForm({
    defaultValues: initialValues,
    resolver,
  });

  const handleSaveAsDraft = () => {
    const formValues = mapValuesToDraft(methods.getValues());
    onSaveDraft(formValues);
  };

  const submit = values => {
    const preparedValues = mapValues(values) as unknown as ITouristRoutesItem;

    preparedValues.ogTitle = values.ogTitle;
    preparedValues.ogDescription = values.ogDescription;
    preparedValues.ogType = values.type;
    preparedValues.ogUrl = values.ogUrl;
    preparedValues.ogSiteName = values.ogSiteName;

    onFinish(preparedValues);
  };

  useDeepEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  useDeepEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      const errorsKeys = getCollapseTabsWithError(
        methods.formState.errors,
        collapseFieldsErrors
      );

      setActiveCollpaseTabs(prev =>
        Array.from(new Set([...prev, ...errorsKeys]))
      );
    }
  }, [methods.formState.errors]);

  const lang = methods.watch('lang');

  useDeepEffect(() => {
    if (methods.formState.isDirty) {
      methods.reset({
        ...methods.getValues(),
        tags: [],
        routeType: null,
        routeLevel: null,
      });
    }
  }, [lang]);

  const langValue = isString(lang) ? lang : lang.value;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className='ant-form ant-form-vertical indent-top'
      >
        <Helmet>
          <title>{methods.watch('metaTitle') || 'Default Title'}</title>
          <meta
            name='description'
            content={methods.watch('metaDescription') || 'Default description'}
          />
          <meta name='keywords' content={methods.watch('metaKeywords')} />
          <meta property='og:title' content={methods.watch('ogTitle')} />
          <meta
            property='og:description'
            content={methods.watch('ogDescription') || 'Default OG Description'}
          />
          <meta property='og:image' content={methods.watch('ogImage') || ''} />
          <meta property='og:type' content={methods.watch('ogType') || ''} />
          <meta property='og:url' content={methods.watch('ogUrl') || ''} />
          <meta
            property='og:ogSiteName'
            content={methods.watch('ogSiteName') || ''}
          />
        </Helmet>

        <LangSelectSection />
        <Card title='Общая информация' bordered={false}>
          <Row gutter={[16, 0]}>
            <Col flex={'0 0 100px'}>
              <MainImage
                name='image'
                label='Превью'
                required
                aspectRatio={PREVIEW_ASPECT_RATIO}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputField
                name='name'
                label='Название маршрута'
                required
                placeholder='Введите название'
                showCount
                maxLength={255}
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <CatalogAsyncSelect
                name='tags'
                label='Tеги'
                placeholder='Выберите теги'
                requestParams={{
                  entityType: 'routes',
                  fieldType: 'tags',
                  lang: langValue,
                }}
                mode='multiple'
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <CatalogRegionSelect
                name='region'
                label='Регион'
                placeholder='Регион'
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputField
                name='shortDescription'
                label='Краткое описание'
                required
                placeholder='Описание'
                showCount
                maxLength={300}
              />
            </Col>
          </Row>
        </Card>
        <Collapse
          bordered={false}
          expandIconPosition='end'
          onChange={(value: string[]) => setActiveCollpaseTabs(value)}
          activeKey={activeCollpaseTabs}
        >
          <Collapse.Panel header='Описание маршрута' key='description'>
            <Row gutter={[24, 0]} align='bottom'>
              <Col xs={{ span: 24 }} md={{ span: 8 }}>
                <CatalogAsyncSelect
                  name='routeType'
                  label='Тип маршрута'
                  placeholder='Выберите тип маршрута'
                  requestParams={{
                    entityType: 'routes',
                    fieldType: 'routeTypes',
                    lang: langValue,
                  }}
                  required
                />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 8 }}>
                <CatalogAsyncSelect
                  name='routeLevel'
                  label='Сложность'
                  placeholder='Не указана'
                  requestParams={{
                    entityType: 'routes',
                    fieldType: 'routeLevels',
                    lang: langValue,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <TextAreaField
                  label='Введение'
                  name='intro'
                  placeholder='Текст'
                  showCount
                  maxLength={700}
                  rows={10}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <ContentField
                  isRoutesForm
                  enabledTypes={[
                    ContentTypeEnum.image,
                    ContentTypeEnum.collage,
                    ContentTypeEnum.verticalCollage,
                    ContentTypeEnum.gallery,
                    ContentTypeEnum.video,
                    ContentTypeEnum.widget,
                    ContentTypeEnum.quote,
                    ContentTypeEnum.separator,
                    ContentTypeEnum.htmlCode,
                    ContentTypeEnum.reference,
                  ]}
                  label='Описание'
                  name='content'
                  placeholder='Начните печатать текст'
                  required
                  withWidgetsDescription
                  withPreparingForModeration
                />
              </Col>
            </Row>
          </Collapse.Panel>
          <Collapse.Panel
            header='Точки маршрута'
            key='points'
            className='ant-touristRoutes-collapse'
          >
            <TouristRoutePoints name='points' />
          </Collapse.Panel>
          <Collapse.Panel header='Траектория' key='polyline'>
            <CreateRouteMap name='polyline' />
          </Collapse.Panel>
          <Collapse.Panel
            header='Продолжительность маршрута'
            key='routeDuration'
          >
            <Row gutter={[48, 0]}>
              <Col md={{ span: 8 }} xs={{ span: 24 }}>
                <InputField
                  name='days'
                  label='Количество дней'
                  placeholder='0'
                  type='days'
                  required
                />
              </Col>
              <Col md={{ span: 8 }} xs={{ span: 24 }}>
                <DurationField name='duration' label='Время' required />
              </Col>
            </Row>
          </Collapse.Panel>
          <Collapse.Panel header='Туроператор' key='externalUrl'>
            <Row>
              <Col span={24}>
                <InputField
                  name='externalUrl'
                  label='Ссылка на туроператора'
                  addonBefore={<GlobalOutlined />}
                  placeholder={'Укажите ссылку'}
                />
              </Col>
            </Row>
          </Collapse.Panel>
          <Collapse.Panel header='SEO' key='seo'>
            <SEO />
          </Collapse.Panel>
          <Collapse.Panel header='Публикация' key='status'>
            <Status status={initialValues.status} isStatusesLimited />
          </Collapse.Panel>
        </Collapse>
        <FormActions>
          <Button onClick={cancelButtonHandler} disabled={isLoading}>
            Отмена
          </Button>
          {isDraft && (
            <Button onClick={handleSaveAsDraft} loading={isLoading}>
              Сохранить как черновик
            </Button>
          )}
          <Button type='primary' htmlType='submit' loading={isLoading}>
            Сохранить
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
};
